import { forwardRef, useEffect, useRef, type HTMLAttributes, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

// Família Kanban ARA (padrão lanes) — shells PRESENTACIONAIS que detêm o estilo.
// A lógica (drag, dados, seleção) fica no módulo consumidor, que passa ref +
// props via spread. Mudar o estilo aqui propaga pra todos os kanbans.
//
// Anatomia: KanbanBoard (linha rolável) › KanbanLane (contêiner cinza r14 +
// header) › KanbanCard (card branco r10, sombra leve, sem borda).

// ---------- Board ----------
export type KanbanBoardProps = HTMLAttributes<HTMLDivElement>;

export const KanbanBoard = forwardRef<HTMLDivElement, KanbanBoardProps>(
  ({ className, children, ...rest }, ref) => (
    <div ref={ref} className={cn('flex items-start gap-3 overflow-x-auto', className)} {...rest}>
      {children}
    </div>
  ),
);
KanbanBoard.displayName = 'KanbanBoard';

// ---------- Lane ----------
export interface KanbanLaneProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title: ReactNode;
  /** Número ou rótulo (ex.: "3/10") na pílula de contagem. */
  count?: ReactNode;
  /** Ícone mono opcional à esquerda do título (14px, muted). */
  icon?: LucideIcon;
  /** Texto secundário após o título (ex.: "· 4 abertas"). */
  meta?: ReactNode;
  /** Slot à direita do header (ex.: botão "+"). */
  headerRight?: ReactNode;
  /** Destaque de drop (dnd) — anel accent. */
  isOver?: boolean;
  children: ReactNode;
}

export const KanbanLane = forwardRef<HTMLDivElement, KanbanLaneProps>(
  ({ title, count, icon: Icon, meta, headerRight, isOver, className, children, ...rest }, ref) => {
    // Área de cards preenche a altura da tela; se transbordar, rola internamente
    // com barra auto-hide (via .scroll-auto-hide + listener global). max-height é
    // calculada do topo do container até a base da viewport pra não gerar scroll de página.
    const bodyRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
      const el = bodyRef.current;
      if (!el) return undefined;
      // Altura = espaço da área de cards até a base da região visível, medido
      // relativo ao container de scroll (NÃO à viewport) → INDEPENDENTE de scroll.
      // Não escuta scroll de propósito: se crescesse ao rolar, a página aumentaria
      // e daria pra rolar mais → esticava infinito. Assim a coluna enche o espaço
      // abaixo do seletor com altura estável; transbordou, rola dentro (auto-hide).
      // Só recalcula em resize da janela e do container.
      const scroller = (el.closest('main') as HTMLElement | null);
      const fit = () => {
        let avail: number;
        if (scroller) {
          const offsetTop =
            el.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
          avail = scroller.clientHeight - offsetTop - 56;
        } else {
          avail = window.innerHeight - el.getBoundingClientRect().top - 56;
        }
        el.style.height = `${Math.max(160, Math.round(avail))}px`;
      };
      fit();
      // 2º passe assíncrono cobre layout que assenta após o mount (seletor/fontes).
      const t = window.setTimeout(fit, 120);
      window.addEventListener('resize', fit);
      const ro =
        scroller && 'ResizeObserver' in window ? new ResizeObserver(fit) : null;
      if (ro && scroller) ro.observe(scroller);
      return () => {
        window.clearTimeout(t);
        window.removeEventListener('resize', fit);
        if (ro) ro.disconnect();
      };
    }, []);
    return (
      <div
        ref={ref}
        className={cn(
          'flex w-[270px] shrink-0 flex-col overflow-hidden rounded-[14px] bg-surface2',
          isOver && 'ring-2 ring-accent',
          className,
        )}
        {...rest}
      >
        <div className="flex items-center gap-2 px-3.5 pb-2.5 pt-3">
          {Icon && <Icon size={14} strokeWidth={1.5} className="shrink-0 text-text-muted" />}
          <span className="text-[11px] font-medium uppercase tracking-wide text-text-muted">
            {title}
          </span>
          {meta != null && <span className="text-[11px] text-text-muted">{meta}</span>}
          {count != null && (
            <span className="ml-auto rounded-full bg-surface px-2 py-px text-[11px] font-bold text-text-2">
              {count}
            </span>
          )}
          {headerRight && <div className={cn(count == null && 'ml-auto')}>{headerRight}</div>}
        </div>
        <div
          ref={bodyRef}
          // Inset lateral dos cards = 8px simétrico: pl-2 dá o padding esquerdo;
          // à direita, os 8px vêm da própria barra (scroll-overlay-bar força a
          // barra webkit a 8px, transparente em repouso). Sem px-2 dos dois
          // lados pra não SOMAR padding + barra à direita (ficava assimétrico).
          // Ver o racional completo em tokens.css › .scroll-overlay-bar.
          className="scroll-auto-hide scroll-overlay-bar flex min-h-[80px] flex-col gap-1.5 overflow-y-auto pl-2 pb-2.5"
        >
          {children}
        </div>
      </div>
    );
  },
);
KanbanLane.displayName = 'KanbanLane';

// ---------- Card ----------
export type KanbanCardProps = HTMLAttributes<HTMLDivElement>;

export const KanbanCard = forwardRef<HTMLDivElement, KanbanCardProps>(
  ({ className, children, ...rest }, ref) => (
    <div
      ref={ref}
      className={cn(
        'flex flex-col gap-2 rounded-[10px] bg-surface p-3',
        'shadow-[0_1px_3px_rgba(0,0,0,0.06)] transition-shadow',
        'hover:shadow-[0_2px_8px_rgba(0,0,0,0.08)]',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  ),
);
KanbanCard.displayName = 'KanbanCard';

// ---------- Empty state ----------
export function KanbanLaneEmpty({ children }: { children?: ReactNode }) {
  return (
    <div className="py-6 text-center text-[0.8rem] text-text-muted">{children ?? 'Vazia'}</div>
  );
}
